export type ProgressUpdate = { loaded: number; total: number };

type FetchPageResult<T> = {
  items: T[];
  totalPages: number;
};

type FetchPagesInParallelOptions<T> = {
  fetchPage: (page: number) => Promise<FetchPageResult<T>>;
  concurrency?: number;
  onProgress?: (progress: ProgressUpdate) => void;
};

export async function fetchPagesInParallel<T>({
  fetchPage,
  concurrency = 6,
  onProgress
}: FetchPagesInParallelOptions<T>): Promise<T[]> {
  const first = await fetchPage(1);

  if (!Number.isFinite(first.totalPages) || first.totalPages < 0)
    throw new Error(
      `Invalid totalPages from first response (got ${first.totalPages}) — refusing to fan out.`
    );

  if (!Array.isArray(first.items))
    throw new Error(
      "First response missing items[] — refusing to silently drop rows."
    );

  const totalPages = Math.max(1, first.totalPages);
  const pages: T[][] = new Array(totalPages);
  pages[0] = first.items;

  let loaded = 1;
  onProgress?.({ loaded, total: totalPages });

  if (totalPages > 1) {
    let nextPage = 2;
    let aborted = false;

    const worker = async () => {
      while (!aborted) {
        const page = nextPage++;
        if (page > totalPages) return;

        let result: FetchPageResult<T>;
        try {
          result = await fetchPage(page);
        } catch (err) {
          aborted = true;
          throw err;
        }
        if (aborted) return;

        if (!Array.isArray(result.items)) {
          aborted = true;
          throw new Error(
            `Page ${page} response missing items[] — refusing to silently drop rows.`
          );
        }

        pages[page - 1] = result.items;
        loaded++;
        if (aborted) return;
        onProgress?.({ loaded, total: totalPages });
      }
    };

    const workerCount = Math.min(concurrency, totalPages - 1);
    await Promise.all(Array.from({ length: workerCount }, worker));
  }

  return pages.flat();
}
